import { sendTrack } from '@onlive.site/front-core';
import * as Sentry from '@sentry/react';

/**
 * Every way a dial can fail gets its own reason. Before this the UI showed
 * "make sure your Aircall phone is logged in" for every failure, including the
 * ones where the agent was perfectly logged in and the real cause was the phone
 * number format, so support kept chasing session problems that did not exist.
 */
export const AircallDialError = {
    NOT_MOUNTED: 'not_mounted',
    MISSING_NUMBER: 'missing_number',
    INVALID_NUMBER: 'invalid_number',
    NOT_LOGGED_IN: 'not_logged_in',
    LOGIN_CHECK_TIMEOUT: 'login_check_timeout',
    DIAL_FAILED: 'dial_failed',
    // The dial was accepted but the call ended unanswered within seconds, the
    // "no ringback, then it hangs up" case. Nothing in the dial path fails there.
    CALL_DROPPED: 'call_dropped',
};

const LOG_PREFIX = '[aircall]';

/**
 * Notifying from here rather than from the panel means a failure is shown to the
 * agent even when the panel itself is what failed to load, which is exactly the
 * case that used to fail completely silently.
 */
let dialErrorNotifier = null;

export function setDialErrorNotifier(notifier) {
    dialErrorNotifier = notifier;
}

/**
 * Support can turn on the SDK's own verbose logging on a user's machine with
 * `localStorage.setItem('aircall:debug', '1')` plus a reload, with no deploy.
 */
export function isAircallDebugEnabled() {
    try {
        return window.localStorage.getItem('aircall:debug') === '1';
    } catch (error) {
        return false;
    }
}

export function aircallDebugLog(message, detail) {
    if (!isAircallDebugEnabled()) return;
    console.info(`${LOG_PREFIX} ${message}`, detail ?? '');
}

/**
 * Phone numbers are customer PII, so only a masked version is allowed to leave
 * the browser. The full number stays in the local console, where the agent can
 * already see it on screen anyway.
 */
export function maskPhoneNumber(phone) {
    const value = String(phone ?? '').trim();
    if (!value) return '';
    if (value.length <= 4) return '*'.repeat(value.length);
    return `${value.slice(0, 3)}${'*'.repeat(value.length - 5)}${value.slice(-2)}`;
}

/**
 * `data` comes straight from the Aircall SDK, so it may be an Error, a DOM
 * object or something circular. Never let the report itself throw.
 */
function safeSerialize(value) {
    if (value === null || value === undefined) return null;
    if (value instanceof Error) return { name: value.name, message: value.message, stack: value.stack };
    if (typeof value !== 'object') return value;
    try {
        return JSON.parse(JSON.stringify(value));
    } catch (error) {
        return { unserializable: String(value) };
    }
}

/**
 * Snapshot of everything that explains an Aircall failure after the fact, so a
 * Sentry issue is actionable without asking the user to reproduce it.
 */
export function collectAircallDiagnostics() {
    const container = document.querySelector('#aircall-container');
    const iframe = document.querySelector('#aircall iframe');

    return {
        iframeMounted: Boolean(iframe),
        containerMounted: Boolean(container),
        panelVisible: container ? !container.classList.contains('hidden') : false,
        // A blocked third-party iframe reports as online while being unable to
        // reach Aircall, so this rules the trivial case in rather than out.
        browserOnline: typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
        // The workspace is a cross-site iframe: with cookies disabled it cannot
        // see its own session and reports the agent as logged out.
        cookiesEnabled: navigator.cookieEnabled,
        language: navigator.language,
        appVersion: process.env.REACT_APP_VERSION ?? 'unknown',
        environment: process.env.REACT_APP_ENVIRONMENT ?? 'unknown',
        occurredAt: new Date().toISOString(),
    };
}

/**
 * The workspace iframe gets the microphone through delegation from this page,
 * so the permission it runs with is the one this origin holds. Asking here
 * tells a blocked or missing microphone apart from a network that drops the
 * audio, which look identical to the agent. Async, so only for reports that
 * are not shown to the agent and can wait for it.
 */
export async function collectMediaDiagnostics() {
    const diagnostics = { microphonePermission: null, audioInputCount: null };
    try {
        const status = await navigator.permissions?.query({ name: 'microphone' });
        diagnostics.microphonePermission = status?.state ?? null;
    } catch (error) {
        // Some browsers do not support querying the microphone permission.
    }
    try {
        // Without permission browsers hide labels and may collapse the list, so
        // this only reliably tells "no microphone at all" (0) from "some".
        const devices = await navigator.mediaDevices?.enumerateDevices();
        diagnostics.audioInputCount = devices ? devices.filter(({ kind }) => kind === 'audioinput').length : null;
    } catch (error) {
        // Left as null: unknown rather than zero.
    }
    return diagnostics;
}

function buildReport({ reason, rawPhone, dialedPhone, defaultCountry, providerData, context }) {
    return {
        reason,
        rawPhone: rawPhone ?? null,
        dialedPhone: dialedPhone ?? null,
        defaultCountry: defaultCountry ?? null,
        providerData: safeSerialize(providerData),
        ...collectAircallDiagnostics(),
        ...(context ?? {}),
    };
}

function sendToSentry(report, { level, title, cause }) {
    Sentry.withScope((scope) => {
        scope.setLevel(level);
        scope.setTag('feature', 'aircall');
        scope.setTag('aircall.reason', report.reason);
        scope.setContext('aircall', {
            ...report,
            rawPhone: maskPhoneNumber(report.rawPhone),
            dialedPhone: maskPhoneNumber(report.dialedPhone),
        });
        // Grouped by reason rather than by stack: the stack is always this file,
        // so a per-reason fingerprint is what keeps the issues separable.
        scope.setFingerprint(['aircall', report.reason]);
        if (cause) scope.setExtra('cause', safeSerialize(cause));
        Sentry.captureMessage(title);
    });
}

/**
 * Reports a failure that stopped the call. Returns the report so the caller can
 * hand it to the UI layer and show a message that matches the actual reason.
 */
export function reportAircallDialError(details) {
    const report = buildReport(details);

    console.error(`${LOG_PREFIX} dial error: ${report.reason}`, report, details.cause ?? '');

    sendToSentry(report, {
        level: 'error',
        title: `Aircall dial error: ${report.reason}`,
        cause: details.cause,
    });

    sendTrack('aircall_dial_error', {
        ...(details.context ?? {}),
        reason: report.reason,
        phone: maskPhoneNumber(report.dialedPhone || report.rawPhone),
        defaultCountry: report.defaultCountry,
        providerData: report.providerData ? JSON.stringify(report.providerData) : null,
        iframeMounted: report.iframeMounted,
        browserOnline: report.browserOnline,
        cookiesEnabled: report.cookiesEnabled,
        appVersion: report.appVersion,
    });

    // Reporting must never be broken by the UI layer it notifies.
    try {
        dialErrorNotifier?.(report);
    } catch (error) {
        console.error(`${LOG_PREFIX} failed to notify dial error`, error);
    }

    return report;
}

/**
 * Reports something suspicious that did not stop the call, so it shows up in
 * Sentry as a trend without interrupting an agent whose call is going through.
 */
export function reportAircallWarning(details) {
    const report = buildReport(details);

    console.warn(`${LOG_PREFIX} warning: ${report.reason}`, report, details.cause ?? '');

    sendToSentry(report, {
        level: 'warning',
        title: `Aircall warning: ${report.reason}`,
        cause: details.cause,
    });

    return report;
}
